import { resolveTextSize, TEXT_SIZE_ATTRIBUTE, textSizeOf, textSizeStore, type TextSize, type TextSizeStore } from './text-size-rules.js';

// The theme, stored under the medrec console's old key (nl-theme). The Line / Emoji icon switch is gone (2026-10-11 look match): the medrec
// console draws emoji beside its words everywhere and SVG line icons only on the header's tool buttons, so this console does the same.
const write = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* storage blocked */ } };

/** The root element's attribute that holds every transition still while the theme changes (console-theme.css, at its end). */
export const THEME_SWITCHING_ATTRIBUTE = 'data-theme-switching';
// How many holds have started this visit: only the newest one's second frame takes the attribute off.
let switches = 0;

/** THE SWITCH IN ONE FRAME (ported 2026-10-18 from the medrec console's ui-fix-3 lane, its PR #173, lib/theme.ts): the root carries
 *  `data-theme-switching` from just before the new theme is applied until two frames later, so no colour fades between the themes (a press
 *  started a fade on every button and row at once while the page itself swapped at once, so for 150 ms rows showed dark ink on still-light
 *  rows); the first frame paints the new theme, the second lets the browser settle with motion still off, then every hover fade is back. A
 *  second press inside the two frames keeps the hold and restarts its count. */
export function holdTransitionsForSwitch(
  root: Pick<Element, 'setAttribute' | 'removeAttribute'>,
  frame: (next: () => void) => void = (next) => requestAnimationFrame(() => next()),
): void {
  root.setAttribute(THEME_SWITCHING_ATTRIBUTE, '');
  const mine = (switches += 1);
  frame(() => frame(() => {
    if (mine === switches) root.removeAttribute(THEME_SWITCHING_ATTRIBUTE);
  }));
}

export function toggleTheme(): 'light' | 'dark' {
  const next = document.documentElement.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
  holdTransitionsForSwitch(document.documentElement);
  document.documentElement.setAttribute('data-theme', next);
  write('nl-theme', next);
  return next;
}

// THE TEXT SIZE (ported 2026-10-12 from the medrec console, Rod's pick C there; the rules are text-size-rules.ts). The step is the root
// element's `data-text-size` (absent at Standard), which styles/text-size.css turns into the `--ts` factor every scaled length reads and the
// build's scaled width rules key on. index.html puts it on the root before the first paint (the saved pick, else the window's step); it
// changes only when the person picks a step in the header. Saved per browser under nl-text-size, as the theme is under nl-theme; with the
// storage blocked a pick holds for the visit (the theme's rule above).
const safeStorage = (): Storage | null => { try { return window.localStorage; } catch { return null; } };
let store: TextSizeStore | null = null;
const sizes = (): TextSizeStore => (store ??= textSizeStore(safeStorage()));

/** The step on screen and whether the console chose it by itself: as index.html set it for this visit, then the person's picks. Read once,
 *  so a resize never changes it; every header (each page draws its own) reads the same answer. */
let current: { size: TextSize; auto: boolean } | null = null;
export function textSizeState(): { size: TextSize; auto: boolean } {
  if (current !== null) return current;
  const flag = (window as { __ncTextSizeAuto?: unknown }).__ncTextSizeAuto;
  if (typeof flag === 'boolean') {
    current = { size: textSizeOf(document.documentElement.getAttribute(TEXT_SIZE_ATTRIBUTE)) ?? 'standard', auto: flag };
  } else {
    // The inline script did not run (a page built without it): the same rule here, put on the root now.
    current = resolveTextSize(sizes().pick(), window.innerWidth);
    applyTextSize(current.size);
  }
  return current;
}

/** The step on screen. */
export function textSize(): TextSize {
  return textSizeState().size;
}

function applyTextSize(size: TextSize): void {
  if (size === 'standard') document.documentElement.removeAttribute(TEXT_SIZE_ATTRIBUTE);
  else document.documentElement.setAttribute(TEXT_SIZE_ATTRIBUTE, size);
}

/** A person's pick: saved, on the root at once, and from then on theirs (the window's default no longer applies). */
export function setTextSize(size: TextSize): void {
  sizes().setPick(size);
  current = { size, auto: false };
  applyTextSize(size);
}

/** Whether this browser has seen the one-line hint ("Text size set to Larger for your screen; change it here."). */
export function textSizeHintSeen(): boolean {
  return sizes().hintSeen();
}
export function setTextSizeHintSeen(): void {
  sizes().setHintSeen();
}
